// useCourseIngest — ingest orchestration hook extracted from course.tsx.
//
// Manages the full lifecycle: extract outline → review → generate (streaming ingest).
// The hook owns internal state (progress, lint, outline draft) and reports
// view-level state changes (status, error, result) via callbacks.
//
// Session 47: View monolith decomposition.

import { useCallback, useRef, useState } from 'react';
import type { CourseDraft, CurriculumOutline, LintReport } from '@examforge/shared/schemas';
import type { TFunction } from '../../i18n/use-t.js';
import {
  getPdf,
  putDraft,
  putResult,
  type IngestResultRecord,
} from '../../storage/idb.js';
import {
  extractOutline,
  lockOutline,
  cancelIngest as cancelIngestApi,
} from '../../api/ingest.js';
import type { IngestResult } from '../../api/ingest.js';
import { toFriendlyMessage } from '../friendly-error.js';
import { computeSourceHash } from '../source-hash.js';
import { streamIngestForDraft } from '../staged-ingest-client.js';
import {
  initialProgress,
  reduceIngestEvent,
  type IngestProgressState,
} from '../../components/ingest-progress.js';
import { useStore } from '../store/index.js';
import { reportBehaviorError } from '../report-behavior-error.js';
import { extract } from '@examforge/parser';
import { useToast } from '../../components/toast.js';
import { useNotify } from './use-notify.js';

/** Status values the hook can transition to (subset of CourseView's Status). */
export type IngestStatus = 'generating' | 'extracting' | 'reviewing' | 'done' | 'idle' | 'error';

export interface CourseIngestCallbacks {
  onStatusChange: (status: IngestStatus) => void;
  onError: (msg: string | null) => void;
  onResultReady: (record: IngestResultRecord) => void;
}

/**
 * Hook managing the extract → review → generate ingest lifecycle.
 *
 * The hook owns internal state (streaming progress, lint report, outline draft)
 * and uses callbacks to update view-level state (status, error, result record).
 * Uses a ref for callbacks to avoid re-creating functions on every render.
 */
export function useCourseIngest(
  draft: CourseDraft | null,
  t: TFunction,
  callbacks: CourseIngestCallbacks,
) {
  // Ref for stable callback access (avoids stale closures without re-creating functions)
  const cb = useRef(callbacks);
  cb.current = callbacks;
  // 19BM-C: regenerateConfirm migrated from window.confirm to sticky toast.
  const toast = useToast();
  const notify = useNotify();

  // Internal state — only the hook and its consumers read these
  const [ingestProgress, setIngestProgress] = useState<IngestProgressState>(initialProgress);
  const [lintReport, setLintReport] = useState<LintReport | undefined>(undefined);
  const [outlineDraft, setOutlineDraft] = useState<CurriculumOutline | null>(null);
  const parsedSourceTextRef = useRef<string>('');
  const abortRef = useRef<AbortController | null>(null);

  // ── generate ──────────────────────────────────────────────────────────

  /**
   * Run the full ingest pipeline. When `outline` is provided, Phase 2
   * (extract_cases) is skipped (Mode A — outline already confirmed).
   * This is the single shared implementation for both "skip review" direct
   * generation and "confirm outline → generate" flows.
   */
  const generate = useCallback(async (outline?: CurriculumOutline): Promise<void> => {
    if (!draft) return;
    // P2-9 (Session 19AN): guard against rapid double-click.
    if (abortRef.current) return;
    cb.current.onError(null);
    setIngestProgress(initialProgress);
    setLintReport(undefined);
    cb.current.onStatusChange('generating');
    // 19BN Phase D D6: notify ingest start (info · non-persistent — transient
    // signal that a generation kicked off).
    notify({ source: 'ingest', level: 'info', title: '开始生成课程', message: draft.title, persistent: false });
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const slots = draft.sources ?? [];
      if (slots.length === 0) {
        cb.current.onError(t('course.uploadMissing'));
        cb.current.onStatusChange('error');
        return;
      }
      // Session 19AD: gather every slot's File blob from IDB.
      const slotFiles = new Map<string, File>();
      for (const slot of slots) {
        const blob = await getPdf(slot.sha256);
        if (blob) {
          const f = blob instanceof File ? blob : new File([blob], slot.filename, { type: blob.type });
          slotFiles.set(slot.id, f);
        }
      }
      if (slotFiles.size === 0) {
        cb.current.onError(t('course.uploadMissing'));
        cb.current.onStatusChange('error');
        return;
      }
      let finalResult: IngestResult | null = null;
      // 19BN Phase 2: preflight credit check before main ingest (~104 points).
      const balance = useStore.getState().balance;
      if (balance !== null && balance < 104) {
        cb.current.onError(t('account.insufficientCredits'));
        cb.current.onStatusChange('error');
        // 19BN Phase D D6: notify the message center on balance-insufficient
        // interception (persistent — the user needs to act: recharge).
        notify({ source: 'account', level: 'warning', title: '点数不足', message: `当前余额 ${balance} 点，生成需 104 点`, persistent: true });
        return;
      }
      for await (const ev of streamIngestForDraft({
        draft,
        slotFiles,
        signal: ac.signal,
        ...(outline ? { outline } : {}),
      })) {
        // G5: capture runId for cancel notification to backend.
        if (ev.type === 'workflow-started') {
          useStore.getState().setRegenState({ regenRunId: ev.runId });
        }
        setIngestProgress((prev) => reduceIngestEvent(prev, ev));
        if (ev.type === 'pipeline-complete') {
          const stampedPkg = { ...ev.package, sourceHash: computeSourceHash(draft) };
          finalResult = {
            ok: true,
            package: stampedPkg,
            meta: {
              inputTokens: ev.meta.inputTokens ?? 0,
              outputTokens: ev.meta.outputTokens ?? 0,
              durationMs: ev.meta.totalDurationMs,
              providerName: ev.meta.providerName,
              // DEBT-19BO+-LINTREPORT-IDB-PERSIST (批次 C4 · 2026-06-29): persist
              // lintReport into IDB via meta so it survives reload. Previously
              // only the setLintReport call below kept it in volatile React
              // state — a refresh lost the verdict, so the done-view LintSummary
              // (course.tsx passes lintReport to the result view) could no longer
              // show the quality verdict. (NOTE: an older draft of this comment
              // blamed deriveOverallState; that 八态机 is dead code —
              // DEBT-19BO+-OVERALL-STATE-DEAD-CODE — so the real impact was on
              // LintSummary, not state derivation.)
              ...(ev.meta.lintReport ? { lintReport: ev.meta.lintReport } : {}),
            },
          };
          if (ev.meta.lintReport) setLintReport(ev.meta.lintReport);
        }
      }
      if (!finalResult) throw new Error('ingest stream ended without pipeline-complete');
      const record: IngestResultRecord = {
        courseId: draft.id,
        generatedAt: new Date().toISOString(),
        result: finalResult,
      };
      await putResult(record);
      cb.current.onResultReady(record);
      cb.current.onStatusChange('done');
      // 19BN Phase 5: notify message center on ingest completion.
      notify({ source: 'ingest', level: 'success', title: 'Course generation complete', message: draft.title, persistent: true });
      // 19BN Phase D D6: notify points deducted (info · non-persistent — the
      // balance hero already shows the new balance; this is the audit record).
      notify({ source: 'account', level: 'info', title: '点数已扣除', message: `生成「${draft.title}」`, persistent: false });
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        cb.current.onStatusChange('idle');
        return;
      }
      cb.current.onError(toFriendlyMessage(e));
      cb.current.onStatusChange('error');
      // 19BN Phase 5: notify message center on ingest/outline failure.
      notify({ source: 'ingest', level: 'error', title: 'Course generation failed', message: draft.title, persistent: true });
    } finally {
      abortRef.current = null;
    }
  }, [draft, t, notify]);

  // ── cancelIngest ──────────────────────────────────────────────────────

  /** Abort client SSE + best-effort backend cancel via v4 cancel API. */
  function cancelIngest(): void {
    abortRef.current?.abort();
    const runId = useStore.getState().regenRunId;
    if (runId) {
      cancelIngestApi(runId).catch(() => { /* best-effort, ignore */ });
    }
  }

  // ── startExtract ──────────────────────────────────────────────────────

  /** Parse source files → call extractOutline API → transition to reviewing. */
  const startExtract = useCallback(async (): Promise<void> => {
    if (!draft) return;
    cb.current.onError(null);
    cb.current.onStatusChange('extracting');
    try {
      const slots = draft.sources ?? [];
      if (slots.length === 0) {
        cb.current.onError(t('course.uploadMissing'));
        cb.current.onStatusChange('error');
        return;
      }
      // Parse source files to markdown.
      const parts: string[] = [];
      for (const slot of slots) {
        const blob = await getPdf(slot.sha256);
        if (!blob) continue;
        const file = blob instanceof File ? blob : new File([blob], slot.filename, { type: blob.type });
        if (slot.format === 'pdf' || slot.format === 'pptx' || slot.format === 'docx') {
          try {
            const result = await extract(file);
            parts.push(result.markdown);
          } catch (parseErr) {
            // 19BM Work-area A: report the swallowed PDF parse failure so the
            // operator gains visibility (previously DEV-only console.error — prod
            // was silent, the file was silently dropped from the parts array and
            // ingest proceeded with partial source material).
            const viewPath = typeof location !== 'undefined' ? location.pathname : '';
            void reportBehaviorError({
              viewPath,
              userAction: 'PDF 解析',
              code: 'ingest-pdf-parse',
            });
            // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
            if (import.meta.env.DEV) console.error('[course-ingest] PDF parse failed for slot:', slot.filename, parseErr);
          }
        } else if (slot.format === 'md' || slot.format === 'txt') {
          parts.push(await file.text());
        }
      }
      const sourceText = parts.join('\n\n');
      if (!sourceText.trim()) {
        cb.current.onError(t('course.uploadMissing'));
        cb.current.onStatusChange('error');
        return;
      }
      parsedSourceTextRef.current = sourceText;

      // 19BN Phase 2: preflight for extract-outline (~24 points).
      const outlineBalance = useStore.getState().balance;
      if (outlineBalance !== null && outlineBalance < 24) {
        cb.current.onError(t('account.insufficientCredits'));
        cb.current.onStatusChange('error');
        // 19BN Phase D D6: notify balance-insufficient (extract path).
        notify({ source: 'account', level: 'warning', title: '点数不足', message: `当前余额 ${outlineBalance} 点，生成大纲需 24 点`, persistent: true });
        return;
      }
      const outline = await extractOutline({
        courseId: draft.id,
        courseTitle: draft.title,
        sourceText,
        // 19BO+: thread subject + language into the extract call so the
        // outline is generated with the right discipline tone + content
        // language from the first call (were previously dropped).
        ...(draft.discipline ? { discipline: draft.discipline } : {}),
        ...(draft.customDiscipline ? { customDiscipline: draft.customDiscipline } : {}),
        ...(draft.contentLanguage ? { contentLanguage: draft.contentLanguage } : {}),
      });
      setOutlineDraft(outline);
      cb.current.onStatusChange('reviewing');
      // 19BN Phase D D6: notify extract-outline complete (non-persistent —
      // the outline review view is the primary feedback).
      notify({ source: 'ingest', level: 'success', title: '大纲生成完成', message: draft.title, persistent: false });
    } catch (e) {
      cb.current.onError(toFriendlyMessage(e));
      cb.current.onStatusChange('error');
      // 19BN Phase 5: notify message center on ingest/outline failure.
      notify({ source: 'ingest', level: 'error', title: 'Course generation failed', message: draft.title, persistent: true });
    }
  }, [draft, t, notify]);

  // ── confirmOutline ────────────────────────────────────────────────────

  /** Lock outline via v4 API, persist frozen outline, then delegate to generate. */
  const confirmOutline = useCallback(async (outline: CurriculumOutline): Promise<void> => {
    const frozenOutline: CurriculumOutline = {
      ...outline,
      source: 'llm_generated_frozen',
      generatedAt: outline.generatedAt || new Date().toISOString(),
    };

    try {
      // Best-effort lock call — if backend unavailable (local dev), proceed
      // with the locally frozen outline. The ingest pipeline still receives it.
      await lockOutline({ courseId: draft!.id, outline: frozenOutline });
    } catch (lockErr) {
      // 19BM Work-area A: report the swallowed lock-outline failure so the
      // operator gains visibility (previously DEV-only console.error — prod was
      // silent, ingest continued with a locally-frozen outline but the backend
      // never recorded it, a silent state desync).
      const viewPath = typeof location !== 'undefined' ? location.pathname : '';
      void reportBehaviorError({
        viewPath,
        userAction: '大纲锁定',
        code: 'ingest-lock-outline',
      });
      // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
      if (import.meta.env.DEV) console.error('[course-ingest] lock-outline failed (non-blocking):', lockErr);
    }

    // Persist frozen outline to draft so downstream views read it.
    // DEBT-19BO+-FROZEN-OUTLINE-PERSIST: putDraft writes to IDB (single chokepoint —
    // also flips saveStatus to 'pending' for authed users via its internal
    // markPending). Previously only setDraft updated in-memory Zustand state, so
    // a page refresh between confirm and generate lost the frozen outline.
    if (draft) {
      const updatedDraft = { ...draft, curriculumOutline: frozenOutline };
      useStore.getState().setDraft(updatedDraft);
      void putDraft(updatedDraft).catch((e) => {
        // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
        if (import.meta.env.DEV) console.error('[course-ingest] putDraft frozen outline failed:', e);
      });
    }

    return generate(frozenOutline);
  }, [draft, generate]);

  // ── regenerateConfirm ─────────────────────────────────────────────────

  /** Sidebar "重新生成" — user confirms, then goes through extract → review → confirm.
   *  19BM-C: window.confirm migrated to sticky toast + action. Destructive
   *  (regenerate overwrites existing content). */
  function regenerateConfirm(): void {
    toast.warn(t('course.regenerateConfirm'), {
      sticky: true,
      action: { label: t('course.regenerate'), onClick: () => { void startExtract(); } },
    });
  }

  // ── acceptPasted ──────────────────────────────────────────────────────

  /** Accept a pasted JSON result — persists to IDB and reports ready. */
  async function acceptPasted(result: IngestResult): Promise<void> {
    if (!draft) return;
    const record: IngestResultRecord = {
      courseId: draft.id,
      generatedAt: new Date().toISOString(),
      result,
    };
    await putResult(record);
    cb.current.onResultReady(record);
    cb.current.onStatusChange('done');
  }

  /**
   * Hydrate internal state from an IDB-loaded record (DEBT-19BO+-LINTREPORT-
   * IDB-PERSIST · 批次 C4 · 2026-06-29).
   *
   * Called by the view on mount / navigation when it reads the persisted
   * IngestResultRecord from IDB. Without this, lintReport lived only in the
   * volatile React state set during `generate`, so a reload lost the verdict
   * and the done-view LintSummary (course.tsx passes lintReport to the result
   * view) could no longer show the quality verdict. (NOTE: an older draft of
   * this comment blamed deriveOverallState; that 八态机 is dead code —
   * DEBT-19BO+-OVERALL-STATE-DEAD-CODE — so the real impact was on LintSummary,
   * not state derivation.)
   *
   * Currently hydrates lintReport only; ingestProgress / outlineDraft are not
   * restored (a completed run doesn't need streaming progress, and outline is
   * re-read from draft.curriculumOutline elsewhere). Add fields here as the
   * local-first story grows.
   */
  const hydrateFromRecord = useCallback((record: IngestResultRecord): void => {
    setLintReport(record.result.meta.lintReport);
  }, []);

  return {
    generate,
    startExtract,
    confirmOutline,
    cancelIngest,
    acceptPasted,
    regenerateConfirm,
    hydrateFromRecord,
    ingestProgress,
    lintReport,
    outlineDraft,
  };
}
